// Watch: every episode opens as a full-screen 9:16 reel.
// - Swipe / scroll up for the next episode of the show, down for the previous one (also ↑ ↓ keys and the arrow buttons).
// - Tap to pause, double-tap the left or right side to skip 10 s. Seek bar, speed, quality and CC sit on the video.
// - Right side: Like, Save, Share, Episodes (opens the episode list).
// - When an episode ends, the next one starts by itself (if Auto-play is on); a locked episode opens the Plus screen.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type WheelEvent } from 'react';
import { api } from '../lib/api';
import { useZoomLock } from '../lib/antipiracy';
import { useQuery } from '../lib/query';
import { Link, back, navigate } from '../lib/router';
import { getState, hasPlus, setState, toast, useApp } from '../lib/store';
import { useDesktop } from '../lib/useMedia';
import { useVerticalSwipe } from '../lib/useSwipe';
import { haptic } from '../lib/haptics';
import { prefetchEpisode } from '../lib/prefetch';
import { Icon } from '../components/Icon';
import { Player } from '../components/Player';
import { AppShell, ErrorBox } from '../components/Shell';
import type { UpNextItem } from '../lib/types';
import { EpisodeSheet, useSheet } from '../components/EpisodeSheet';

// The page remounts for each episode. A swipe or key press sets the direction for the next mount;
// arriving from anywhere else (a reel, the show page, a link) is an "enter": one soft fade, no slide.
type Dir = 'up' | 'down' | 'none';
let pendingDir: Dir | null = null;
const AUTO_NEXT_S = 3;

export function Watch({ showId, no }: { showId: string; no: number }) {
  useZoomLock(true);
  const desktop = useDesktop();
  const plus = useApp((s) => hasPlus(s));
  const progress = useApp((s) => s.progress);
  const autoNext = useApp((s) => s.settings.autoScroll);
  const show = useQuery('show:' + showId, () => api.show(showId));
  const play = useQuery(`play:${showId}:${no}:${plus}`, () => api.play(showId, no));
  const [ended, setEnded] = useState(false);
  const [count, setCount] = useState(AUTO_NEXT_S);
  const episodesBtn = useRef<HTMLButtonElement>(null);
  const sheetCtl = useSheet(episodesBtn);
  const sheet = sheetCtl.state;
  // How this episode arrived (captured once per mount).
  const [arrival] = useState<Dir | 'enter'>(() => { const d = pendingDir; pendingDir = null; return d ?? 'enter'; });
  // Set when the dark loading frame was on screen, so the episode fades in where it is instead of sliding again.
  const bufferedRef = useRef(false);
  const liked = useApp((s) => (play.data ? !!s.likes[play.data.episode.id] : false));
  const saved = useApp((s) => !!s.saves[showId]);
  const lastWheel = useRef(0);

  useEffect(() => {
    if (play.data && !play.data.ok) navigate(`/plus?show=${showId}&ep=${no}`, { replace: true });
  }, [play.data, showId, no]);

  const s = show.data;
  const episodes = s?.episodes || [];
  const total = episodes.length;
  const nextNoForPrefetch = no + 1;
  const isLocked = (n: number) => {
    const x = episodes.find((e) => e.no === n);
    return !!x && s?.access === 'plus' && !x.isFreePreview && !plus;
  };

  const goTo = useCallback((n: number, fromKey = false) => {
    if (!s) return;
    if (n < 1) { toast('This is the first episode'); return; }
    if (n > total) { setEnded(true); return; }
    // Keyboard moves happen often and should feel instant: no slide animation for them.
    pendingDir = fromKey ? 'none' : n > no ? 'down' : 'up';
    if (isLocked(n)) navigate(`/plus?show=${s.id}&ep=${n}`);
    else navigate(`/watch/${s.id}/${n}`, { replace: true });
  }, [s, total, no, plus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ↑ ↓ keys move between episodes (← → and Space are handled by the player).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (sheet) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); goTo(no + 1, true); }
      if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); goTo(no - 1, true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, no, sheet]);

  const swipe = useVerticalSwipe({
    onNext: () => goTo(no + 1),
    onPrev: () => goTo(no - 1),
    atStart: no <= 1,
    disabled: !!sheet,
    ignore: (t) => !!(t as HTMLElement | null)?.closest?.('.ep-controls, .ep-sheet, .reel-side, .end-card, .reels-top'),
  });

  // Get the next episode ready shortly after this one starts (details always; video only on fast, unmetered networks).
  useEffect(() => {
    if (!s || !play.data?.ok) return;
    // Both neighbours, so a swipe either way lands on a ready episode.
    const t = window.setTimeout(() => {
      if (nextNoForPrefetch <= total && !isLocked(nextNoForPrefetch)) prefetchEpisode(s.id, nextNoForPrefetch, plus);
      if (no > 1 && !isLocked(no - 1)) prefetchEpisode(s.id, no - 1, plus);
    }, 800);
    return () => window.clearTimeout(t);
  }, [s, play.data, plus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Up next: similar episodes from other shows, fetched when this one ends.
  const [upNext, setUpNext] = useState<UpNextItem[] | null>(null);
  const [holdAuto, setHoldAuto] = useState(false);
  useEffect(() => {
    if (!ended) return;
    let alive = true;
    api.upNext(showId, no).then((items) => {
      if (!alive) return;
      setUpNext(items);
      items.filter((i) => !i.locked).slice(0, 2).forEach((i) => prefetchEpisode(i.show.id, i.episode.no, plus));
    }).catch(() => setUpNext([]));
    return () => { alive = false; };
  }, [ended, showId, no, plus]);
  const playSuggestion = (i: UpNextItem) => {
    haptic('tap');
    if (i.locked) navigate(`/plus?show=${i.show.id}&ep=${i.episode.no}`);
    else navigate(`/watch/${i.show.id}/${i.episode.no}`);
  };
  const replay = () => { setEnded(false); setHoldAuto(false); setReplayKey((k) => k + 1); };
  const [replayKey, setReplayKey] = useState(0);

  // Auto-play the next episode after a short countdown (stops as soon as they browse the suggestions).
  const nextNo = no + 1;
  const canAutoNext = ended && autoNext && !holdAuto && nextNo <= total && !isLocked(nextNo);
  useEffect(() => {
    if (!canAutoNext) return;
    setCount(AUTO_NEXT_S);
    const t = window.setInterval(() => setCount((c) => c - 1), 1000);
    return () => window.clearInterval(t);
  }, [canAutoNext]);
  useEffect(() => { if (canAutoNext && count <= 0) goTo(nextNo); }, [canAutoNext, count, goTo, nextNo]);

  const ep = play.data?.episode;
  const onProgress = useCallback((pos: number) => {
    if (!ep) return;
    api.heartbeat({ showId: ep.showId, episodeId: ep.id, positionS: pos, durationS: ep.durationS });
  }, [ep]);
  const onEnded = useCallback(() => {
    if (!ep) return;
    api.heartbeat({ showId: ep.showId, episodeId: ep.id, positionS: ep.durationS, durationS: ep.durationS });
    setEnded(true);
  }, [ep]);

  if (play.error || show.error) return <AppShell><ErrorBox message={play.error || show.error || ''} onRetry={play.reload} /></AppShell>;
  const containerClass = 'reels watch-reel' + (desktop ? ' reels-desktop' : '') + (arrival === 'enter' ? ' reel-enter' : '');
  if (!play.data || !s || !play.data.ok) {
    // Same container as the ready state (React keeps it), dark frame + small spinner: never a light loading page,
    // never a second animation when the episode arrives.
    const firstPaint = !bufferedRef.current;
    bufferedRef.current = true;
    return (
      <AppShell mobileHeader={false} bottomNav={false} flush>
        <div className={containerClass}>
          <div className="reels-top">
            <div className="watch-top-left">
              <button type="button" className="icon-btn glass" onClick={() => back(s ? '/show/' + s.slug : '/home')} aria-label="Back"><Icon name="back" /></button>
              {s && <span className="watch-top-title"><b>Ep {no} of {total}</b><small>{s.title}</small></span>}
            </div>
          </div>
          <div key="buffer" className={'reel-slide slide-' + (firstPaint && arrival !== 'enter' ? arrival : 'none')}>
            <div className="player player-episode" style={{ '--poster': s?.color || '#1e1e20' } as CSSProperties}>
              <div className="player-stage"><div className="player-frame" /><span className="buffer-spinner" role="status" aria-label="Loading video" /></div>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }
  const slideClass = bufferedRef.current ? 'fade' : arrival === 'enter' ? 'none' : arrival;

  const e = play.data.episode;
  const p = progress[e.id];
  const startAt = p && p.positionS < e.durationS - 5 ? p.positionS : 0;

  const fromControls = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.('.ep-controls, .ep-sheet, .reel-side, .end-card');
  const onWheel = (ev: WheelEvent) => {
    if (sheet || fromControls(ev.target)) return;
    const now = Date.now();
    if (now - lastWheel.current < 650 || Math.abs(ev.deltaY) < 20) return;
    lastWheel.current = now;
    goTo(no + (ev.deltaY > 0 ? 1 : -1));
  };

  const toggleLike = () => { haptic('tap'); setState((st) => {
    const likes = { ...st.likes };
    if (likes[e.id]) delete likes[e.id]; else likes[e.id] = true;
    return { likes };
  }); };
  const toggleSave = () => {
    haptic('tap');
    const was = !!getState().saves[s.id];
    setState((st) => {
      const saves = { ...st.saves };
      if (saves[s.id]) delete saves[s.id]; else saves[s.id] = true;
      return { saves };
    });
    toast(was ? 'Removed from your library' : 'Saved to your library');
  };
  const share = () => navigator.clipboard?.writeText(`https://kuchnaya.in/show/${s.slug}`)
    .then(() => toast('Link copied'), () => toast('Copy this link: kuchnaya.in/show/' + s.slug));

  const nextLocked = nextNo <= total && isLocked(nextNo);

  const feed = (
    <div className={containerClass + (ended ? ' has-up-next' : '')} onWheel={onWheel} {...swipe.handlers}>
      <div className="reels-top">
        <div className="watch-top-left">
          <button type="button" className="icon-btn glass" onClick={() => back('/show/' + s.slug)} aria-label="Back"><Icon name="back" /></button>
          <span className="watch-top-title"><b>Ep {e.no} of {total}</b><small>{s.title}</small></span>
        </div>
      </div>

      <div key={e.id} ref={swipe.slideRef} className={'reel-slide slide-' + slideClass}>
        <Player
          key={replayKey}
          episodeKey={e.id}
          caption={e.hook}
          color={s.color}
          durationS={e.durationS}
          startAt={replayKey ? 0 : startAt}
          watermark={play.data.watermark}
          playlistUrl={play.data.playlistUrl}
          mode="episode"
          onProgress={onProgress}
          onEnded={onEnded}
        />
        {ended && (
          <div className="end-card up-next" role="dialog" aria-label="Episode finished">
            <div className="up-next-head">
              <b>{nextNo <= total ? `Episode ${e.no} done` : `You finished ${s.title}`}</b>
              <button type="button" className="text-link" onClick={replay}><Icon name="play" size={14} /> Watch again</button>
            </div>
            {nextNo <= total && (
              nextLocked ? (
                <Link to={`/plus?show=${s.id}&ep=${nextNo}`} className="up-next-main locked">
                  <span className="ep-thumb" style={{ '--poster': s.color } as CSSProperties}><Icon name="lock" size={16} /></span>
                  <span className="ep-text"><small>Next in this show</small><b>Ep {nextNo} · {episodes[nextNo - 1]?.title}</b></span>
                  <span className="btn btn-sm btn-primary">Unlock with Plus</span>
                </Link>
              ) : (
                <button type="button" className="up-next-main" onClick={() => goTo(nextNo)}>
                  <span className="ep-thumb" style={{ '--poster': s.color } as CSSProperties}>{nextNo}</span>
                  <span className="ep-text">
                    <small>{canAutoNext ? `Next in this show · starts in ${Math.max(count, 0)}s` : 'Next in this show'}</small>
                    <b>Ep {nextNo} · {episodes[nextNo - 1]?.title}</b>
                  </span>
                  <span className="up-next-play" aria-hidden="true"><Icon name="play" size={16} /></span>
                </button>
              )
            )}
            <div className="up-next-label"><b>More like this</b>{canAutoNext && <button type="button" className="text-link" onClick={() => setHoldAuto(true)}>Stay here</button>}</div>
            <ul className="up-next-row" onPointerDown={() => setHoldAuto(true)} onScroll={() => setHoldAuto(true)}>
              {(upNext ?? Array.from({ length: 4 }, () => null)).map((i, idx) => (
                <li key={i ? i.show.id : 'sk' + idx}>
                  {i ? (
                    <button type="button" className="up-tile" onClick={() => playSuggestion(i)} style={{ '--poster': i.show.color } as CSSProperties}
                      aria-label={`${i.show.title}, episode ${i.episode.no}: ${i.episode.title}${i.locked ? ', Plus' : ''}`}>
                      <span className="up-tile-badges">{i.locked ? <span className="badge badge-plus">PLUS</span> : i.episode.isFreePreview && i.show.access === 'plus' ? <span className="badge badge-free">FREE</span> : null}</span>
                      <span className="up-tile-play" aria-hidden="true"><Icon name={i.locked ? 'lock' : 'play'} size={16} /></span>
                      <span className="up-tile-text"><small>{i.reason} · Ep {i.episode.no}</small><b>{i.show.title}</b></span>
                    </button>
                  ) : <span className="up-tile skel-block" aria-hidden="true" />}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="reel-side">
        <div className="reel-actions">
          <button type="button" onClick={toggleLike} aria-pressed={liked} className={liked ? 'on' : ''}><Icon name="heart" size={28} /><span>Like</span></button>
          <button type="button" onClick={toggleSave} aria-pressed={saved} className={saved ? 'on' : ''}><Icon name="bookmark" size={28} /><span>{saved ? 'Saved' : 'Save'}</span></button>
          <button type="button" onClick={share}><Icon name="share" size={28} /><span>Share</span></button>
          <button type="button" ref={episodesBtn} onClick={sheetCtl.open} aria-haspopup="dialog"><Icon name="list" size={28} /><span>Episodes</span></button>
        </div>
      </div>

      <div className="reel-info">
        <span className="creator"><span className="avatar sm accent">{s.creator.name[0]}</span><span><b>{e.title}</b><small>{s.title} · {s.creator.name}</small></span></span>
        <small className="muted">{desktop ? 'Scroll or press ↓ for the next episode. Space to pause, ← → to skip 10 seconds.' : 'Swipe up for the next episode. Double-tap the sides to skip 10 seconds.'}</small>
      </div>

      {sheet && (
        <EpisodeSheet
          title={s.title}
          color={s.color}
          episodes={episodes}
          currentNo={e.no}
          isLocked={isLocked}
          closing={sheet === 'closing'}
          onClose={() => sheetCtl.close()}
          onPick={(n) => { sheetCtl.dismissNow(); if (n !== e.no) goTo(n); }}
        />
      )}
    </div>
  );

  return <AppShell mobileHeader={false} bottomNav={false} flush>{feed}</AppShell>;
}
